<template>
    <div class="current-coach">
        <div v-if="!noTitle" class="section-title">{{ $tc('coach.coach', 1) }}</div>

        <template v-if="coach && !coachMissing">
            <div class="cc-card">
                <div class="cc-info" @click="goToCoach" v-ripple>
                    <div class="cc-name text-truncate">{{ coach.coachName }}</div>
                    <div class="cc-description text-truncate">{{ coach.coachDescription }}</div>
                </div>

                <button-tooltip :tooltip="$t('coach.send_message')">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-on="on" v-bind="attrs" depressed height="40" class="cc-chat-btn" @click="showChat">
                            <v-icon size="18">mdi-message-processing-outline</v-icon>
                            <span v-if="noRead" class="cc-chat-dot" />
                        </v-btn>
                    </template>
                </button-tooltip>
            </div>
            <chat-dialog :clientId="coachId" ref="chat" />
        </template>

        <div v-else class="cc-empty" @click="goToCoaches" v-ripple>
            <v-icon size="18" color="accent">mdi-alert-decagram-outline</v-icon>
            <span class="cc-empty-text">
                {{ coachMissing ? $t('coach.coach_unavailable') : $t('coach.no_hired_coach') }}
            </span>
            <span class="cc-empty-action">{{ $t('coach.see_coaches') }}</span>
        </div>
    </div>
</template>

<script>
import {mapState} from "vuex";
import {findChat} from "@/utils/Messages";
import ChatDialog from "@/components/student/ChatDialog";
import ButtonTooltip from "@/components/common/ButtonTooltip";

export default {
    name: "CurrentCoach",
    components: {ChatDialog, ButtonTooltip},
    props: {
        noTitle: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            noRead: false,
        }
    },
    async mounted() {
        const vm = this
        await vm.checkLastMessage()
    },
    computed: {
        ...mapState(['coachMissing']),
        coach(vm = this) {
            return vm.account?.studentSubscription || null
        },
        coachId(vm = this) {
            return vm.coach?.coachId || null
        },
        studentId(vm = this) {
            return vm.currentUser?.uid || null
        }
    },
    methods: {
        showChat() {
            const vm = this
            vm.$refs.chat.show()
        },
        async checkLastMessage() {
            const vm = this
            if (!vm.coach) return;
            if (!vm.coachId || !vm.studentId) return;
            const chat = await findChat('privateChats', 'coach_student', vm.coachId, vm.studentId)
            if (!chat) return

            const lastMessage = chat.data().lastMessage || null
            vm.noRead = lastMessage?.from === vm.studentId ? false : !!lastMessage && !lastMessage.read
        },
        goToCoach() {
            const vm = this
            vm.pushRoute({name: 'student.coach-profile', params: {uid: vm.coachId}})
        },
        goToCoaches() {
            const vm = this
            vm.pushRoute({name: 'student.coaches'})
        }
    },
    watch: {
        account() {
            this.checkLastMessage()
        }
    }
}
</script>

<style scoped>

.current-coach {
    display: flex;
    flex-direction: column;
}

.section-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 10px;
}

.cc-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    border-radius: 12px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
}

.cc-info {
    flex: 1;
    min-width: 0;
    cursor: pointer;
}

.cc-name {
    font-size: var(--fs-body);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.cc-description {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.cc-chat-btn {
    position: relative;
    flex-shrink: 0;
    min-width: 44px !important;
    border-radius: 10px !important;
    background: var(--brand-gradient) !important;
    color: #000 !important;
}

.cc-chat-btn >>> .v-icon {
    color: #000 !important;
}

.cc-chat-dot {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--v-error-base);
    border: 1.5px solid var(--v-surface-base);
}

.cc-empty {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px solid rgba(51, 153, 153, 0.3);
    background: rgba(51, 153, 153, 0.08);
    cursor: pointer;
}

.cc-empty-text {
    flex: 1;
    min-width: 0;
    font-size: 12px;
    line-height: 1.4;
    color: var(--v-txtSecondary-base);
}

.cc-empty-action {
    flex-shrink: 0;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    color: var(--v-accent-base);
    background: rgba(51, 153, 153, 0.16);
}

</style>